/**
 *  @file       utils/assert-params.js
 *  @author     KC Erb <kcerb@kingsds.network>
 *  @date       January 2020
 *
 *  This module provides methods for asserting the shape and types of an object.
 */
'use strict';

const path = require('path');
const semver = require('semver');
const { BigNumber } = require('dcp/types');

const { DCPError } = require('dcp/common/dcp-error');

const customTypes = {
  'amount': validateAmount,
  'semver': validateSemver,
  'date': validateDate,
  // subject to change in the future, maybe...
  'opaqueId': (x) => x,
  'any': (x) => x,
};

var progName;
if (require('dcp/common/dcp-env').isBrowserPlatform)
  progName = window.location.href;
else
  progName = path.basename(process.mainModule ? process.mainModule.filename : 'node-repl', '.js');

/**
 * @todo FIX THIS - decouple validation and marshaling
 *
 * This function marshals wire-formatted data into the final data types used by the operation, 
 * while simultaneously validating said data.
 *
 * The sub-functions throw exceptions when trying to marshal and invalid data is encountered. These
 * exceptions are then caught and used to generate an exception in this function.
 */
function assertParams(obj, parameters, locationLabel) {
  for (const paramName in parameters) {
    if (parameters.hasOwnProperty(paramName)) {
      let reqVal = obj[paramName];
      const spec = parameters[paramName];
      try {
        obj[paramName] = assertParamPassesSpec(reqVal, spec)
      } catch (e) {
        if (exports.assertTriggersDebugger)
          debugger; // allow-debugger
        if (String(reqVal) === "[object Object]") reqVal = JSON.stringify(reqVal);
        throw new DCPError(
          `"${paramName}" with value "${reqVal}" in ${locationLabel || progName} failed to pass spec. Error message was: ` +
            e.message,
          'EBADPARAM',
        );
      }
    }
  }
}

/**
 * Check that a given value passes a specification of that value.
 * @param {*} val The value to check
 * @param {obj} spec Must have `type` key to check type, other validation keys are type specific.
 * @returns the default val if present and val is undefined. If val is anything else, returns the passed in val
 *          if `spec.type` is primitive or the result of new spec.type(val).
 * @throws when val could not be constructed or does not pass spec (specs are expected to throw as well)
 */
function assertParamPassesSpec(val, spec) {
  if (spec.hasOwnProperty('default') && val === undefined) return spec.default

  if (typeof spec.type === 'string') {
    val = validatePrimitive(val, spec)
  } else {
    val = new spec.type(val)
    if (!val) throw new Error(`Failed to construct ${spec.type}.`)
  }
  if (typeof spec.validate === 'function') spec.validate(val)
  return val
}

/** Validate that a value meets a "primitive" or "pseudoprimitive" spec
 *  @param val  {*}             Value to test
 *  @param spec {object}        requirements the value must meet
 *  @param spec.type {string}   Either the name of a js primitive type, or a DCP pseudoprimitive
 *
 *  Spec types and descriptors:
 *  'string'  regex
 *  'number'  min, max
 *  'bigint'  min, max
 *  'boolean' (nothing extra)
 *  'array'   (nothing extra)
 * Custom types:
 *  If the type is a key in the customTypes object, the corresponding function will be used to validate it.
 */
function validatePrimitive(val, spec) {
  if (spec.type in customTypes) {
    return customTypes[spec.type](val);
  }
  // simple type checking
  else if (['string', 'number', 'bigint', 'boolean'].includes(spec.type)) {
    if (typeof val !== spec.type) throw new Error(`Expected ${spec.type}, got: ${typeof val}.`)
  }

  if (Array.isArray(spec.in) && !spec.in.includes(val)) {
    throw new Error(`Expected ${val} to be one of: ${spec.in}`);
  }

  // primitive validation
  switch (spec.type) {
    case 'string':
      if (spec.regex instanceof RegExp && !spec.regex.test(val))
        throw new Error(`String ${val} did not match regex ${spec.regex}.`)
      break;
    case 'number':
      if (val > spec.max) throw new Error(`Number must not be > ${spec.max}, got: ${val}.`)
      if (val < spec.min) throw new Error(`Number must not be < ${spec.min}, got: ${val}.`)
      break;
    case 'object':
      // recurse via `parameters` key
      if (typeof spec.parameters === 'object')
        assertParams(val, spec.parameters)
      break;

    /* -- fake primitives -- */
    case 'array':
      if (!Array.isArray(val)) throw new Error(`Expected array but ${val} didn't pass the "Array.isArray" check.`)
      break;
    case 'boolean':
    case 'bigint':
      // Type checked earlier. Avoid falling through and throwing on a known type.
      break;
    default:
      throw new Error(`Unknown types encoutered ${spec.type}`);
  }

  return val;
}

/**
 * Validate and coerce an "amount" value
 * @param {string | number | BigNumber} amount - Value to process
 * @return {BigNumber}
 */
function validateAmount(amount) {
  return new BigNumber(amount);
}

function validateSemver(val) {
  if (semver.valid(val)) {
    return val;
  }

  throw new TypeError('Invalid semver format');
}

function validateDate(value) {
  return new Date(value);
}

Object.assign(module.exports, {
  assertParams,
  assertParamPassesSpec,
  validatePrimitive,
  validateAmount,
  validateDate,
});

exports.assertTriggersDebugger = !!require('process').env.DCP_ASSERT_TRIGGERS_DEBUGGER; /* sync: dcp-assert.js */
